import React from 'react';
import { gspx } from '@rsos/gainsight';
import { REFRESH_USER_TOKEN_ERROR } from '@rsos/sinatra/src/actions/types';
import { checkStatus, parseJSON } from '@rsos/utils';
import { getAPIHost } from '@rsos/utils/metaTags';
import {
  captureException,
  captureExceptionWithScope,
} from '@rsos/utils/sentry';
import serializeRequestError from '@rsos/utils/serializeRequestError';
import { notifyError } from '@rsos/utils/toastUtils';
import fetchRetry from '../fetchRetry';
import { store } from '../store';
import {
  SAVE_PSAP_INFO,
  SET_SIGNATURE,
  GET_PSAP_INFO_START,
  GET_PSAP_INFO_SUCCESS,
  GET_PSAP_INFO_FAILED,
  CREATE_PSAP_GEOFENCE_START,
  CREATE_PSAP_GEOFENCE_SUCCESS,
  CREATE_PSAP_GEOFENCE_FAILED,
} from './actionTypes';
import { clear, forcedLogOut } from './index';

const baseURL = `${getAPIHost()}/v1/capstone`;
// Reuse the login page's established query-param flow so it can remove stale
// toasts and show the specific "Your session has expired" guidance once.
const SESSION_EXPIRED_REDIRECT_PATH = '/?session_expired=true';
const AUTHENTICATION_ERROR_STATUSES = new Set([401, 403]);

// fetchRetry rejects with the Redux action returned by refreshUserToken when
// token renewal fails. Match both the action type and its nested HTTP status so
// an unrelated PSAP/network error cannot be mistaken for an expired session.
const isRefreshTokenAuthenticationError = error =>
  error?.type === REFRESH_USER_TOKEN_ERROR &&
  AUTHENTICATION_ERROR_STATUSES.has(error?.error?.response?.status);

// TODO: consolidate capstone backend requests into /libs/

export const createPSAPGeofenceStart = () => ({
  type: CREATE_PSAP_GEOFENCE_START,
});

export const createPSAPGeofenceFail = error => {
  const errMsg = () => {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html:
            'An error occurred. Please try again and <a href="mailto:support@rapidsos.com">contact support</a> if the error repeats.',
        }}
      ></div>
    );
  };
  notifyError({ message: errMsg() });

  return {
    type: CREATE_PSAP_GEOFENCE_FAILED,
    error,
  };
};

export const createPSAPGeofenceSuccess = () => ({
  type: CREATE_PSAP_GEOFENCE_SUCCESS,
});
// TODO: this set of actions should be renamed since geofences aren't handled during signup
export const createPSAPgeofence = psapData => dispatch => {
  dispatch(createPSAPGeofenceStart());

  fetch(`${baseURL}/psaps/`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(psapData),
  })
    .then(checkStatus)
    .then(parseJSON)
    .then(() => dispatch(createPSAPGeofenceSuccess()))
    .catch(async err => {
      const serializedError = await serializeRequestError(err);
      captureException(err);
      dispatch(createPSAPGeofenceFail(serializedError));
    });
};

export const savePSAPInfo = psapInfo => ({
  type: SAVE_PSAP_INFO,
  psapInfo,
});

export const setSignature = signature => ({
  type: SET_SIGNATURE,
  signature,
});

// Get PSAP Info
export const getPSAPInfoStart = () => ({
  type: GET_PSAP_INFO_START,
});

export const getPSAPInfoFail = error => {
  // Session-expiry errors bypass this action. This message is reserved for a
  // genuine failure to load agency data, such as a network or server error.
  const errMsg = () => {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html:
            "We couldn't load your agency information. Please check your " +
            'connection and try again. If the problem continues, contact ' +
            'RapidSOS Support.',
        }}
      ></div>
    );
  };
  notifyError({ message: errMsg() });
  return {
    type: GET_PSAP_INFO_FAILED,
    error,
  };
};

// TODOv4 Spotted this while working on Johannesburg, code duplication
// here an in emergency_data.
export const getPSAPInfoSuccess = psapInfo => ({
  type: GET_PSAP_INFO_SUCCESS,
  psapInfo,
});

export const getPSAPInfo = psapName => dispatch => {
  dispatch(getPSAPInfoStart());

  return fetchRetry(`${baseURL}/psaps/${psapName}`)
    .then(checkStatus)
    .then(async response => {
      return await response.json();
    })
    .then(res => {
      // First update Redux state
      const action = dispatch(getPSAPInfoSuccess(res));

      // Now that state is updated, identify user in Gainsight PX
      gspx.identify();

      return action;
    })
    .catch(async err => {
      const serializedError = await serializeRequestError(err);
      const { auth } = store.getState();
      const { tokenExpiration, refreshExpiration } = auth;
      captureExceptionWithScope(err, {
        extra: {
          accessTokenExpiration: tokenExpiration,
          refreshTokenExpiration: refreshExpiration,
        },
      });
      if (isRefreshTokenAuthenticationError(err)) {
        // Finish the existing revoke/local-cleanup flow before navigation so
        // the login page cannot restore the same stale session again.
        await dispatch(clear);
        window.location.assign(SESSION_EXPIRED_REDIRECT_PATH);
      } else if (AUTHENTICATION_ERROR_STATUSES.has(err.status)) {
        // Preserve the historical handling for direct PSAP 401/403 responses,
        // including a request that remains unauthorized after token refresh.
        dispatch(forcedLogOut);
      } else {
        // Preserve failure actions, telemetry, and retry guidance for all
        // non-authentication PSAP failures.
        dispatch(getPSAPInfoFail(serializedError));
      }
    });
};
